import React from "react";
import { MapPin, Users, CalendarClock, Clock } from "lucide-react";
import Reveal from "./Reveal";
import { venues } from "../mock/venues";
import { modelsFor, modelsWithPhotos } from "./Models";

const mapsUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

const HeroVenueCard = ({ venue, delay }) => {
  const t = venue.theme;
  const total = modelsFor(venue.slug).length;
  const faces = modelsWithPhotos(venue.slug).slice(0, 5);

  return (
    <Reveal delay={delay}>
      <article
        data-testid={`hero-venue-${venue.slug}`}
        className={`flex h-full flex-col overflow-hidden rounded-xl border ${t.rule} ${t.bg}`}
      >
        <div className="aspect-[16/10] w-full overflow-hidden">
          <img
            src={venue.lead}
            alt={venue.name}
            className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
          />
        </div>

        <div className="flex flex-1 flex-col p-5 text-left">
          <h2 className={`font-serif text-xl leading-tight text-foreground`}>{venue.name}</h2>
          <p className={`mt-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] ${t.accent}`}>
            <MapPin className="h-3 w-3" /> {venue.suburb}
          </p>

          <a
            href={mapsUrl(venue.contact.address)}
            target="_blank"
            rel="noreferrer"
            data-testid={`hero-map-${venue.slug}`}
            className={`group mt-4 block overflow-hidden rounded-lg border ${t.rule}`}
          >
            <img
              src={`/media/maps/${venue.slug}.jpg`}
              alt={`Map of ${venue.name}`}
              loading="lazy"
              className="h-[104px] w-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <span
              className={`block px-2.5 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] ${t.chip}`}
            >
              Get direction
            </span>
          </a>

          <dl className="mt-4 space-y-1.5 text-xs">
            <div className="flex items-center gap-2">
              <CalendarClock className={`h-3.5 w-3.5 flex-shrink-0 ${t.accent}`} />
              <dt className="sr-only">Established</dt>
              <dd className="text-muted-foreground">{venue.established}</dd>
            </div>
            <div className="flex items-center gap-2">
              <Users className={`h-3.5 w-3.5 flex-shrink-0 ${t.accent}`} />
              <dt className="sr-only">Ladies</dt>
              <dd className="font-semibold text-foreground">{total} ladies</dd>
            </div>
            {venue.contact.hours ? (
            <div className="flex items-center gap-2">
              <Clock className={`h-3.5 w-3.5 flex-shrink-0 ${t.accent}`} />
              <dt className="sr-only">Hours</dt>
              <dd className="text-muted-foreground">{venue.contact.hours}</dd>
            </div>
            ) : null}
          </dl>

          <div className="mt-4 flex items-center gap-1.5 border-t border-black/10 pt-4">
            {faces.map((m) => (
              <img
                key={m.img}
                src={m.img}
                alt={m.name}
                title={m.age ? `${m.name}, ${m.age}` : m.name}
                loading="lazy"
                className={`h-9 w-9 flex-shrink-0 rounded-full border object-cover ${t.rule}`}
              />
            ))}
            <span className={`ml-1 text-[0.65rem] font-semibold ${t.accent}`}>
              +{Math.max(total - faces.length, 0)}
            </span>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

const heroVenues = venues;

export const HeroVenues = () => (
  <div
    data-testid="hero-venue-snapshots"
    className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
  >
    {heroVenues.map((v, i) => (
      <HeroVenueCard key={v.slug} venue={v} delay={i * 70} />
    ))}
  </div>
);

export default HeroVenues;
